/**
 * DropdownTabUI - 下拉Tab组件
 * 提供可配置的下拉菜单，支持动态添加项目、hover效果和点击回调
 */

import { BootstrapUI, bootstrapUI, ElementOptions } from '../bootstrapui';

export interface DropdownTabItem {
    /** 项目ID */
    id?: string;
    /** 项目标题 */
    title: string;
    /** 点击回调函数 */
    onClick: () => void;
    /** 是否禁用 */
    disabled?: boolean;
    /** 自定义样式类 */
    className?: string|string[];
    /** 图标 */
    icon?: string | HTMLElement;
    /** 图标名称 */
    iconName?: string;
    /** 是否为默认选中项 */
    isDefault?: boolean;
    /** 数据事件类别 */
    dataEventCategory?: string;
    /** 数据事件标签 */
    dataEventLabel?: string;
    /** 多语言键 */
    i18nKey?: string;
}

export interface DropdownTabOptions extends ElementOptions {
    /** 主标题 */
    title?: string;
    /** 初始项目列表 */
    items?: DropdownTabItem[];
    /** 是否有子菜单 */
    hasChild?: true | false;
    /** 是否默认展开 */
    defaultOpen?: boolean;
    /** 下拉菜单样式类 */
    dropdownClass?: string;
    /** 项目容器样式类 */
    itemsContainerClass?: string;
    /** 主按钮样式类 */
    buttonClass?: string;
    /** 是否显示下拉箭头 */
    showArrow?: boolean;
    /** 下拉方向 */
    direction?: 'down' | 'up' | 'left' | 'right';
    /** 最大高度 */
    maxHeight?: string;
    /** 是否可关闭 */
    closable?: boolean;
    /** 主按钮图标 */
    iconName?: string;
    /** 组件唯一标识符 */
    id?: string;
    /** 是否记住当前选中项 */
    remberCurrent?: boolean;
}

export class DropdownTabUI {
    private ui: BootstrapUI;
    private container: HTMLElement;
    private button: HTMLElement;
    private dropdown: HTMLElement;
    private itemsContainer!: HTMLElement;
    private options: DropdownTabOptions;
    private items: DropdownTabItem[] = [];
    private isOpen: boolean = false;
    private onClickCallbacks: ((item: DropdownTabItem, index: number) => void)[] = [];
    private dropdownOpenListener: (event: Event) => void;
    private currentSelectedItem: DropdownTabItem | null = null;

    constructor(options: DropdownTabOptions = {}) {
        this.ui = bootstrapUI;
        this.options = {
            title: 'Dropdown Tab',
            items: [],
            defaultOpen: false,
            showArrow: true,
            direction: 'down',
            maxHeight: '200px',
            closable: true,
            remberCurrent: false,
            id: `dropdown-tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // 生成唯一ID
            ...options
        };
        this.items = this.options.items || [];

        // 初始化当前选中项
        if (this.options.remberCurrent) {
            this.initializeCurrentSelectedItem();
        }

        this.container = this.createContainer();
        this.button = this.createButton();
        this.dropdown = this.createDropdown();

        // 创建事件监听器
        this.dropdownOpenListener = (event: Event) => {
            const customEvent = event as CustomEvent;
            if (customEvent.detail && customEvent.detail.id !== this.options.id) {
                this.close();
            }
        };

        if (this.options.hasChild) {
            this.itemsContainer = this.createItemsContainer();
            this.setupEventListeners();
            this.populateItems();
        }
    }

    /**
     * 初始化当前选中项
     */
    private initializeCurrentSelectedItem(): void {
        // 查找标记为默认的项目
        const defaultItem = this.items.find(item => item.isDefault);
        if (defaultItem) {
            this.currentSelectedItem = defaultItem;
        } else if (this.items.length > 0) {
            // 如果没有默认项，选择第一项
            this.currentSelectedItem = this.items[0];
        }
    }

    private createContainer(): HTMLElement {
        const container = this.ui.createElement({
            className: ['dropdown-tab-container', 'position-relative', ...(Array.isArray(this.options.className) ? this.options.className : this.options.className ? [this.options.className] : [])]
        });

        return container;
    }

    private createButton(): HTMLElement {
        const button = this.ui.createElement({
            tag: 'button',
            className: [
                'dropdown-tab-button',
                'btn',
                'btn-outline-primary',
                'd-flex',
                'align-items-center',
                'justify-content-center',
                'w-100',
                ...(Array.isArray(this.options.buttonClass) ? this.options.buttonClass : this.options.buttonClass ? [this.options.buttonClass] : [])
            ],
            textContent: this.options.iconName ? '' : this.options.title, // 如果有图标则不显示文字
            attributes: {
                type: 'button',
                'aria-expanded': 'false',
                'aria-haspopup': 'true'
            },
            i18nKey: this.options.i18nKey
        });

        if (this.options.iconName) {
            const icon = this.ui.createIcon({
                iconName: this.options.iconName,
                size: 'sm',
                className: 'icon-dropdown-tab-button'
            });
            button.appendChild(icon);
        }

        this.container.appendChild(button);
        return button;
    }

    private createDropdown(): HTMLElement {
        const dropdown = this.ui.createElement({
            className: [
                'dropdown-tab-menu',
                'position-absolute',
                'd-none',   // 默认隐藏
                ...(Array.isArray(this.options.dropdownClass) ? this.options.dropdownClass : this.options.dropdownClass ? [this.options.dropdownClass] : [])
            ],
            styles: {
                top: this.options.direction === 'up' ? 'auto' : '100%',
                bottom: this.options.direction === 'up' ? '100%' : 'auto',
                left: this.options.direction === 'right' ? 'auto' : '0',
                right: this.options.direction === 'right' ? '100%' : 'auto',
                zIndex: '1000',
                minWidth: '150px',
                maxHeight: this.options.maxHeight || '200px',
                overflowY: 'auto',
            }
        });

        this.container.appendChild(dropdown);
        return dropdown;
    }

    private createItemsContainer(): HTMLElement {
        const container = this.ui.createElement({
            className: [
                'dropdown-items-container',
                ...(Array.isArray(this.options.itemsContainerClass) ? this.options.itemsContainerClass : this.options.itemsContainerClass ? [this.options.itemsContainerClass] : [])
            ]
        });

        this.dropdown.appendChild(container);
        return container;
    }

    private setupEventListeners(): void {
        // 按钮点击事件
        this.button.addEventListener('click', (event) => {
            event.stopPropagation();
            this.toggle();
        });

        // 点击外部关闭下拉菜单
        document.addEventListener('click', (event) => {
            if (!this.container.contains(event.target as Node)) {
                this.close();
            }
        });

        // 监听其他dropdownTab的打开事件
        window.addEventListener('dropdownTabOpen', this.dropdownOpenListener);

        // // 键盘事件
        // this.button.addEventListener('keydown', (event) => {
        //     if (event.key === 'Enter' || event.key === ' ') {
        //         event.preventDefault();
        //         this.toggle();
        //     } else if (event.key === 'Escape') {
        //         this.close();
        //     }
        // });
    }

    private populateItems(): void {
        if (!this.options.hasChild) {
            return;
        }
        this.itemsContainer.innerHTML = '';

        this.items.forEach((item, index) => {
            const itemElement = this.createItemElement(item, index);
            this.itemsContainer.appendChild(itemElement);
        });
    }

    private createItemElement(item: DropdownTabItem, index: number): HTMLElement {
        const itemElement = this.ui.createElement({
            className: [
                'dropdown-item',
                'd-flex',
                'align-items-center',
                // 'px-3',
                // 'py-2',
                'cursor-pointer',
                'transition-all',
                ...(Array.isArray(item.className) ? item.className : item.className ? [item.className] : []),
                ...(item.disabled ? ['disabled', 'text-muted'] : [])
            ],
            styles: {
                cursor: item.disabled ? 'not-allowed' : 'pointer',
                opacity: item.disabled ? '0.6' : '1',
            }
        });
        if (item.id) {
            itemElement.id = item.id;
        }
        if (item.dataEventCategory) {
            itemElement.dataset.eventCategory = item.dataEventCategory;
        }
        if (item.dataEventLabel) {
            itemElement.dataset.eventLabel = item.dataEventLabel;
        }
        const contentElement = this.ui.createElement({
            tag: 'div',
            className: [
                'dropdown-item-content',
                'd-flex',
                'align-items-center',
            ]
        });
        itemElement.appendChild(contentElement);
        // 添加图标
        if (item.icon) {
            if (typeof item.icon === 'string') {
                const icon = this.ui.createElement({
                    tag: 'span',
                    className: 'icon-dropdown-tab-item',
                    textContent: item.icon
                });
                contentElement.appendChild(icon);
            } else {
                // HTMLElement 类型
                item.icon.classList.add('icon-dropdown-tab-item');
                contentElement.appendChild(item.icon);
            }
        } else if (item.iconName) {
            const icon = this.ui.createIcon({
                iconName: item.iconName,
                size: 'sm',
                className: 'icon-dropdown-tab-item'
            });
            contentElement.appendChild(icon);
        }

        // 添加标题
        const title = this.ui.createElement({
            tag: 'span',
            textContent: item.title,
            className: 'tabitem-title',
            i18nKey: item.i18nKey
        });
        contentElement.appendChild(title);

        // 添加勾选符号（如果是当前选中项且启用了记住功能）
        if (this.options.remberCurrent && this.currentSelectedItem === item) {
            const checkIcon = this.ui.createElement({
                tag: 'span',
                className: ['check-icon'],
                textContent: '✓'
            });
            itemElement.appendChild(checkIcon);
        }

        // 添加点击事件
        if (!item.disabled) {
            itemElement.addEventListener('click', (event) => {
                event.stopPropagation();
                this.handleItemClick(item, index);
            });
        }

        return itemElement;
    }

    private handleItemClick(item: DropdownTabItem, index: number): void {
        // 如果启用了记住功能，更新当前选中项
        if (this.options.remberCurrent) {
            this.currentSelectedItem = item;
        }

        // 执行项目回调
        if (item.onClick) {
            item.onClick();
        }

        // 执行全局回调
        this.onClickCallbacks.forEach(callback => callback(item, index));

        // 关闭下拉菜单
        if (this.options.closable) {
            this.close();
        }

        // 如果启用了记住功能，重新渲染项目以显示勾选状态
        if (this.options.remberCurrent) {
            this.populateItems();
        }
    }

    private toggle(): void {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    private open(): void {
        this.isOpen = true;
        this.dropdown.classList.remove('d-none');
        this.button.setAttribute('aria-expanded', 'true');

        // 添加打开动画
        this.dropdown.style.opacity = '0';
        this.dropdown.style.transform = 'translateY(-10px)';

        requestAnimationFrame(() => {
            this.dropdown.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
            this.dropdown.style.opacity = '1';
            this.dropdown.style.transform = 'translateY(0)';
        });
        // 触发全局事件，通知其他dropdownTab实例
        window.dispatchEvent(new CustomEvent('dropdownTabOpen', {
            detail: {
                id: this.options.id,
                dropdownTab: this
            }
        }));
    }

    private close(): void {
        this.isOpen = false;

        // 添加关闭动画
        this.dropdown.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
        this.dropdown.style.opacity = '0';
        this.dropdown.style.transform = 'translateY(-10px)';

        setTimeout(() => {
            this.dropdown.classList.add('d-none');
            this.button.setAttribute('aria-expanded', 'false');
        }, 200);
    }

    /**
     * 添加下拉项目
     */
    public addItem(title: string | { title: string, i18nKey?: string }, onClickCallback: () => void, options?: Partial<DropdownTabItem>): void {
        let i18nKey = '';
        if (typeof title === 'object') {
            i18nKey = title.i18nKey || '';
            title = title.title;
        }
        const item: DropdownTabItem = {
            title: title,
            onClick: onClickCallback,
            disabled: false,
            isDefault: false,
            i18nKey: i18nKey,
            ...options
        };

        this.items.push(item);

        // 如果启用了记住功能且这是第一个项目，设置为当前选中项
        if (this.options.remberCurrent && !this.currentSelectedItem) {
            this.currentSelectedItem = item;
        }

        // 如果新项目标记为默认项，设置为当前选中项
        if (this.options.remberCurrent && item.isDefault) {
            this.currentSelectedItem = item;
        }

        this.populateItems();
    }

    /**
     * 移除下拉项目
     */
    public removeItem(index: number): boolean {
        if (index >= 0 && index < this.items.length) {
            this.items.splice(index, 1);
            this.populateItems();
            return true;
        }
        return false;
    }

    /**
     * 清空所有项目
     */
    public clearItems(): void {
        this.items = [];
        this.populateItems();
    }

    /**
     * 设置标题
     */
    public setTitle(title: string): void {
        this.options.title = title;

        // 如果有图标，不显示文字标题
        if (this.options.iconName) {
            return;
        }

        const titleElement = this.button.querySelector('span:not(.dropdown-arrow)');
        if (titleElement) {
            titleElement.textContent = title;
        } else {
            this.button.textContent = title;
        }
    }

    /**
     * 添加点击回调
     */
    public onItemClick(callback: (item: DropdownTabItem, index: number) => void): void {
        this.onClickCallbacks.push(callback);
    }

    /**
     * 获取所有项目
     */
    public getItems(): DropdownTabItem[] {
        return [...this.items];
    }

    /**
     * 获取容器元素
     */
    public getContainer(): HTMLElement {
        return this.container;
    }

    /**
     * 设置当前选中项
     */
    public setCurrentSelectedItem(itemId: string): boolean {
        if (!this.options.remberCurrent) {
            return false;
        }

        const item = this.items.find(i => i.id === itemId);
        if (item) {
            this.currentSelectedItem = item;
            this.populateItems(); // 重新渲染以显示勾选状态
            return true;
        }
        return false;
    }

    /**
     * 获取当前选中项
     */
    public getCurrentSelectedItem(): DropdownTabItem | null {
        return this.currentSelectedItem;
    }

    /**
     * 清除当前选中项
     */
    public clearCurrentSelectedItem(): void {
        if (this.options.remberCurrent) {
            this.currentSelectedItem = null;
            this.populateItems();
        }
    }

    /**
     * 销毁组件
     */
    public destroy(): void {
        // 移除事件监听器
        window.removeEventListener('dropdownTabOpen', this.dropdownOpenListener);
        this.onClickCallbacks = [];
        if (this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
    }
}